// V96-4-3 / ADR-0045: SOC 2 policy template set.
//
// Twelve starter policies a small or mid-size company typically needs in
// front of a SOC 2 auditor. Each maps to Trust Services Criteria (TSC ids
// only) and to the OGCF security-baseline controls (SEC-*) it supports.
// Wording is GAC Pro's own; no AICPA text is reproduced (D-96-6). The
// skeletons are deliberately short: a customer edits them into their own
// policy and publishes through policy-ack-service.
//
// Pure data — no runtime imports.
export const SOC2_POLICY_TEMPLATES_VERSION = 'gacpro-soc2-policies@1';
function skeleton(s) {
    return [
        `# ${s.title}`,
        '',
        '## Purpose',
        '',
        s.purpose,
        '',
        '## Scope',
        '',
        s.scope,
        '',
        '## Roles & Responsibilities',
        '',
        ...s.roles.map((r) => `- ${r}`),
        '',
        '## Policy Statements',
        '',
        ...s.statements.map((p) => `- ${p}`),
        '',
        '## Exceptions',
        '',
        'Exceptions must be requested in writing, state the business reason and any compensating measures, be approved by the Security Lead, and carry an expiry date. Approved exceptions are tracked and re-reviewed before they expire.',
        '',
        '## Review',
        '',
        `${s.reviewCadence ?? 'This policy is reviewed at least annually'} and after any significant change to the business, systems, or legal obligations. Changes are approved by management and republished; personnel re-acknowledge material changes.`,
        '',
    ].join('\n');
}
const ALL_PERSONNEL = 'All employees, contractors, and temporary staff, and every system, service, and location that stores, processes, or transmits company or customer data.';
export const SOC2_POLICY_TEMPLATES = [
    {
        key: 'information_security',
        title: 'Information Security Policy',
        related_criteria: ['CC1.1', 'CC1.3', 'CC2.2', 'CC5.3'],
        related_controls: ['SEC-GOV-01', 'SEC-GOV-02', 'SEC-GOV-07'],
        body_markdown: skeleton({
            title: 'Information Security Policy',
            purpose: 'Set the overall direction for protecting company and customer information and establish the policy framework the other security policies sit under.',
            scope: ALL_PERSONNEL,
            roles: [
                'Executive leadership: approves this policy, funds the security program, and reviews security status at least quarterly.',
                'Security Lead: owns the program, maintains the policy set, and reports on risks and control health.',
                'Managers: make sure their teams follow these policies.',
                'All personnel: read, acknowledge, and follow the policies that apply to them.',
            ],
            statements: [
                'The company maintains a written set of security policies, each with a named owner.',
                'Policies are approved by management and published where all personnel can find them.',
                'Personnel acknowledge the policies at hire and whenever a policy changes materially.',
                'Security responsibilities are assigned to named roles, and those roles have the authority to act.',
                'Leadership reviews security risks, incidents, and control results on a regular schedule.',
                'Controls are monitored, and failures are tracked to resolution.',
                'Violations of security policy may lead to disciplinary action up to and including termination.',
            ],
        }),
    },
    {
        key: 'acceptable_use',
        title: 'Acceptable Use Policy',
        related_criteria: ['CC1.1', 'CC2.2', 'CC6.8'],
        related_controls: ['SEC-HR-04', 'SEC-CFG-04', 'SEC-DAT-03'],
        body_markdown: skeleton({
            title: 'Acceptable Use Policy',
            purpose: 'Define how company devices, accounts, networks, and data may be used.',
            scope: 'All personnel and every company-issued or company-accessed device, account, application, and network.',
            roles: [
                'All personnel: use company resources responsibly and report misuse or suspected compromise.',
                'IT / Security: provision managed devices, enforce technical safeguards, and investigate misuse.',
            ],
            statements: [
                'Company resources are used for business purposes; limited personal use must not create risk or cost.',
                'Company work is done on managed devices with disk encryption, screen lock, and current updates.',
                'Accounts and credentials are personal and are never shared.',
                'Customer and confidential data is stored only in approved systems, never on personal accounts or unapproved tools, including unapproved AI services.',
                'Installing unapproved software or disabling security tooling is not allowed.',
                'Lost or stolen devices and suspected compromises are reported to Security immediately.',
                'The company may monitor its systems for security purposes, as permitted by law.',
            ],
        }),
    },
    {
        key: 'access_control',
        title: 'Access Control Policy',
        related_criteria: ['CC6.1', 'CC6.2', 'CC6.3', 'CC6.6'],
        related_controls: ['SEC-IAM-01', 'SEC-IAM-03', 'SEC-IAM-04', 'SEC-IAM-05', 'SEC-IAM-06'],
        body_markdown: skeleton({
            title: 'Access Control Policy',
            purpose: 'Make sure only authorized people and services can reach company systems and data, with the least access they need.',
            scope: 'All production systems, internal applications, cloud accounts, source code, and customer data, and every account (human or service) that can reach them.',
            roles: [
                'System owners: approve access requests and take part in access reviews.',
                'IT / Security: run provisioning, enforce authentication standards, and coordinate reviews.',
                'Managers: request access for new joiners and changes for movers, and notify leavers promptly.',
            ],
            statements: [
                'Access is granted on least privilege and need to know, based on role.',
                'Every access grant has a documented request and approval before it takes effect.',
                'Multi-factor authentication is required for all workforce access to company systems.',
                'Administrative access is limited to a small, named group and is granted just in time where possible.',
                'Access is removed promptly when someone leaves or no longer needs it.',
                'Access to production and sensitive systems is reviewed at least quarterly; inappropriate access is removed.',
                'Service accounts have a named owner, use managed secrets, and are never used interactively.',
            ],
        }),
    },
    {
        key: 'change_management',
        title: 'Change Management Policy',
        related_criteria: ['CC3.4', 'CC5.2', 'CC8.1'],
        related_controls: ['SEC-CHG-01', 'SEC-CHG-02', 'SEC-CHG-03', 'SEC-CHG-04', 'SEC-CHG-05'],
        body_markdown: skeleton({
            title: 'Change Management Policy',
            purpose: 'Make sure changes to production systems are authorized, tested, reviewed, and traceable.',
            scope: 'Application code, infrastructure-as-code, configuration, database schema, and third-party components that run in production.',
            roles: [
                'Engineers: write changes, add tests, and request review.',
                'Reviewers: check changes for correctness and security before approving.',
                'Engineering leadership: owns the change process and approves emergency changes after the fact.',
            ],
            statements: [
                'All production changes go through version control; direct changes in production are not allowed.',
                'Every change is reviewed and approved by someone other than its author before merge.',
                'Protected branches enforce review, passing automated tests, and required checks.',
                'Changes are deployed through an automated pipeline that records what was deployed and when.',
                'Emergency changes may skip normal review only with documented approval and are reviewed within two business days.',
                'Significant changes are assessed for security and customer impact before release.',
                'The change process itself is reviewed at least annually.',
            ],
        }),
    },
    {
        key: 'incident_response',
        title: 'Incident Response Policy',
        related_criteria: ['CC7.3', 'CC7.4', 'CC7.5', 'CC2.3'],
        related_controls: ['SEC-INC-01', 'SEC-INC-02', 'SEC-INC-03', 'SEC-INC-04'],
        body_markdown: skeleton({
            title: 'Incident Response Policy',
            purpose: 'Detect, respond to, and recover from security incidents quickly and consistently, and learn from them.',
            scope: 'Any event that threatens the confidentiality, integrity, or availability of company or customer data or systems.',
            roles: [
                'Incident commander: leads the response, assigns tasks, and decides on escalation.',
                'Security Lead: maintains the plan, runs exercises, and owns post-incident reviews.',
                'Legal / leadership: decide on customer, regulator, and law-enforcement notifications.',
                'All personnel: report suspected incidents right away.',
            ],
            statements: [
                'A written incident response plan defines severity levels, roles, and escalation paths.',
                'Security events are triaged promptly to decide whether they are incidents.',
                'Incidents are tracked from detection through containment, eradication, and recovery.',
                'Affected customers and authorities are notified within contractual and legal deadlines.',
                'Every significant incident gets a blameless post-incident review with tracked follow-up actions.',
                'The plan is exercised at least annually, for example through a tabletop exercise.',
            ],
        }),
    },
    {
        key: 'business_continuity_disaster_recovery',
        title: 'Business Continuity & Disaster Recovery Policy',
        related_criteria: ['CC7.5', 'CC9.1', 'A1.2', 'A1.3'],
        related_controls: ['SEC-BCP-01', 'SEC-BCP-02', 'SEC-BCP-03', 'SEC-BCP-04'],
        body_markdown: skeleton({
            title: 'Business Continuity & Disaster Recovery Policy',
            purpose: 'Keep critical services running, or restore them within agreed objectives, after a disruption.',
            scope: 'Customer-facing services, the systems and data they depend on, and the people and vendors needed to run them.',
            roles: [
                'Engineering leadership: sets recovery objectives and owns the recovery plan.',
                'Service owners: keep backups and recovery runbooks current for their services.',
                'Security Lead: tracks test results and follow-up actions.',
            ],
            statements: [
                'Critical services have documented recovery time and recovery point objectives.',
                'Backups run on a schedule that meets those objectives, are encrypted, and are stored separately from production.',
                'Backup restores are tested at least annually, and results are recorded.',
                'Production runs across redundant infrastructure where the service commitments require it.',
                'Capacity and availability are monitored against commitments, with alerts on sustained risk.',
                'The recovery plan is tested at least annually, and gaps are tracked to resolution.',
            ],
        }),
    },
    {
        key: 'data_classification_handling',
        title: 'Data Classification & Handling Policy',
        related_criteria: ['C1.1', 'C1.2', 'CC6.5', 'CC6.7'],
        related_controls: ['SEC-DAT-03', 'SEC-DAT-04', 'SEC-DAT-01', 'SEC-DAT-02'],
        body_markdown: skeleton({
            title: 'Data Classification & Handling Policy',
            purpose: 'Classify information by sensitivity and set handling, retention, and disposal rules for each class.',
            scope: 'All information the company creates, receives, or stores, in any format or location.',
            roles: [
                'Data owners: classify their data and approve who can access it.',
                'All personnel: handle data according to its classification.',
                'Security Lead: maintains the classification scheme and retention schedule.',
            ],
            statements: [
                'Information is classified as Public, Internal, Confidential, or Restricted.',
                'Customer data is Confidential at minimum; credentials, keys, and regulated personal data are Restricted.',
                'Confidential and Restricted data is encrypted at rest and in transit and stored only in approved systems.',
                'Restricted data is not copied to endpoints, test environments, or third parties without approval.',
                'Data is kept only as long as the retention schedule or contracts require.',
                'Data past retention is securely deleted, and disposal of media and devices is recorded.',
            ],
        }),
    },
    {
        key: 'vendor_management',
        title: 'Vendor Management Policy',
        related_criteria: ['CC9.2', 'CC3.2'],
        related_controls: ['SEC-GOV-04'],
        body_markdown: skeleton({
            title: 'Vendor Management Policy',
            purpose: 'Manage the security risk that comes from vendors and service providers.',
            scope: 'Every third party that stores, processes, or can access company or customer data, or that critical services depend on.',
            roles: [
                'Vendor owner: the employee who requests the vendor and owns the relationship.',
                'Security Lead: assesses vendor risk and keeps the vendor inventory.',
                'Legal / Finance: make sure contracts carry the required security and privacy terms.',
            ],
            statements: [
                'All vendors are recorded in a vendor inventory with an owner and a risk tier.',
                'Vendors are assessed before onboarding, with depth based on the data and access they receive.',
                'Contracts with higher-risk vendors include security, confidentiality, and breach-notification terms.',
                'Higher-risk vendors are reassessed at least annually, including a review of their independent audit reports where available.',
                'Vendor access is limited to what the service requires and removed when the relationship ends.',
            ],
        }),
    },
    {
        key: 'hr_security',
        title: 'Human Resources Security Policy',
        related_criteria: ['CC1.1', 'CC1.4', 'CC1.5', 'CC2.2'],
        related_controls: ['SEC-HR-01', 'SEC-HR-02', 'SEC-HR-03', 'SEC-HR-04'],
        body_markdown: skeleton({
            title: 'Human Resources Security Policy',
            purpose: 'Make sure people who join, work at, and leave the company are screened, trained, and accountable for security.',
            scope: 'All employees and contractors, from hiring through separation.',
            roles: [
                'People / HR: runs screening, onboarding, and offboarding checklists.',
                'Managers: confirm role requirements and complete joiner, mover, and leaver steps on time.',
                'Security Lead: owns security training content and tracks completion.',
            ],
            statements: [
                'Candidates are background-checked before they get access to production or customer data, where the law allows.',
                'New personnel sign confidentiality terms and acknowledge the code of conduct and security policies.',
                'Security awareness training is completed at hire and at least annually.',
                'Job descriptions set out security responsibilities for roles that carry them.',
                'Leavers have access removed and company assets returned under an offboarding checklist.',
                'Policy violations are handled through a documented disciplinary process.',
            ],
        }),
    },
    {
        key: 'risk_management',
        title: 'Risk Management Policy',
        related_criteria: ['CC3.1', 'CC3.2', 'CC3.3', 'CC3.4', 'CC5.1'],
        related_controls: ['SEC-GOV-03', 'SEC-GOV-06'],
        body_markdown: skeleton({
            title: 'Risk Management Policy',
            purpose: 'Identify, assess, and treat risks to the company\'s objectives and commitments, including fraud risk.',
            scope: 'Security, availability, confidentiality, privacy, and fraud risks across the business, systems, and vendors.',
            roles: [
                'Leadership: sets risk tolerance and approves risk treatment decisions.',
                'Security Lead: runs the risk assessment and keeps the risk register.',
                'Risk owners: carry out and report on treatment plans.',
            ],
            statements: [
                'A formal risk assessment is performed at least annually and after significant changes.',
                'Each risk is rated for likelihood and impact, has an owner, and has a treatment decision: mitigate, transfer, avoid, or accept.',
                'Fraud risk, including misuse of access and manipulation of records, is part of every assessment.',
                'Risk acceptances are approved by leadership and carry an expiry date.',
                'Changes in the business, technology, or vendors that could affect controls are assessed as they happen.',
                'Results of the assessment are reported to leadership.',
            ],
        }),
    },
    {
        key: 'encryption_key_management',
        title: 'Encryption & Key Management Policy',
        related_criteria: ['CC6.1', 'CC6.7', 'C1.1'],
        related_controls: ['SEC-DAT-01', 'SEC-DAT-02', 'SEC-IAM-09', 'SEC-DAT-05'],
        body_markdown: skeleton({
            title: 'Encryption & Key Management Policy',
            purpose: 'Protect data with strong encryption and manage the keys and secrets that make it work.',
            scope: 'Data at rest and in transit in production and backups, and every cryptographic key, certificate, and secret.',
            roles: [
                'Engineering: implements encryption using approved libraries and managed key services.',
                'Security Lead: sets approved algorithms and reviews key access.',
            ],
            statements: [
                'Customer and confidential data is encrypted at rest with industry-standard algorithms.',
                'Data in transit over untrusted networks uses current TLS; outdated protocols are disabled.',
                'Keys are generated and stored in a managed key service or hardware module, not in code or config files.',
                'Access to keys is limited to the services and people that need it and is logged.',
                'Keys and secrets are rotated on a defined schedule and immediately if compromise is suspected.',
                'Home-grown cryptography is not allowed.',
            ],
        }),
    },
    {
        key: 'logging_monitoring',
        title: 'Logging & Monitoring Policy',
        related_criteria: ['CC7.2', 'CC7.3', 'CC4.1', 'CC2.1'],
        related_controls: ['SEC-LOG-01', 'SEC-LOG-02', 'SEC-LOG-03', 'SEC-LOG-04'],
        body_markdown: skeleton({
            title: 'Logging & Monitoring Policy',
            purpose: 'Record security-relevant activity and watch it so that problems are found and acted on quickly.',
            scope: 'Production systems, identity providers, cloud control planes, and security tooling.',
            roles: [
                'Engineering: makes sure systems emit the required logs to the central platform.',
                'Security Lead: defines what is logged, tunes alerts, and reviews monitoring coverage.',
                'On-call responders: acknowledge and triage alerts.',
            ],
            statements: [
                'Security-relevant events, including authentication, privileged actions, and configuration changes, are logged.',
                'Logs are sent to a central platform, protected from tampering, and kept for at least one year.',
                'Access to log data is limited to people who need it.',
                'Alerts cover suspicious activity and are triaged within defined response times.',
                'Logging coverage and alert rules are reviewed at least annually and after significant changes.',
            ],
        }),
    },
];
export function getSoc2PolicyTemplate(key) {
    return SOC2_POLICY_TEMPLATES.find((t) => t.key === key);
}
//# sourceMappingURL=soc2-policy-templates.js.map